import { useForm, useWatch } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import { FinanceRate } from '@kanda-libs/ks-frontend-services';
import { FALLBACK_CID } from 'common/config';
import useQueryParam from 'hooks/useQueryParam';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'store';
import { getDurationsForBnpl } from './JobForm/helpers';
import { filterOutInvalidRates, getAprs, getLowestDuration } from './helpers';

export interface CalculatorFormValues {
  apr: string;
  duration: string;
  deposit: string;
  price: number;
}

export interface CalculatorHook {
  rates: FinanceRate[];
  bnplRates?: FinanceRate[];
  aprs: number[];
  form: ReturnType<typeof useForm<CalculatorFormValues>>;
  onSubmit: (values: StringIndexedObject) => void;
}

export default function useCalculator(): CalculatorHook {
  const [cid] = useQueryParam('cid');
  const [priceParam] = useQueryParam('price');

  const priceInit =
    priceParam && priceParam !== 'null'
      ? Math.round(parseFloat(priceParam) * 100)
      : undefined;

  const form = useForm<CalculatorFormValues>({
    defaultValues: {
      apr: '0',
      price: cid === 'null' ? priceInit || 200000 : priceInit,
    },
  });

  const [apr] = useWatch({
    name: ['apr'],
    control: form.control,
  });

  const [excludeAprs] = useQueryParam('excludeAprs');

  const aprsToExclude = useMemo(
    () => excludeAprs?.split(',') || [],
    [excludeAprs],
  );

  const allRates = useSelector(selectors.rate.getEntities);

  const unfilteredRates = useMemo(() => {
    if (!cid || cid === 'null')
      return allRates[FALLBACK_CID]?.finance_rates || [];
    return allRates[cid || '']?.finance_rates || [];
  }, [allRates, cid]);

  const rates = useMemo(
    () => filterOutInvalidRates(unfilteredRates, aprsToExclude),
    [unfilteredRates, aprsToExclude],
  );

  const aprs = useMemo(() => getAprs(rates), [rates]);

  const bnplRates = useMemo(() => {
    const bnpl = filterOutInvalidRates(unfilteredRates, aprsToExclude, true);
    if (!bnpl || bnpl.length === 0) return undefined;
    return bnpl;
  }, [unfilteredRates, aprsToExclude]);

  // When the APR changes, update the duration to the lowest available
  useEffect(() => {
    if (apr) {
      if (apr === 'BNPL') {
        const durations = getDurationsForBnpl(bnplRates);
        form.setValue('duration', durations[0]);
        return;
      }
      const duration = getLowestDuration(
        unfilteredRates.filter(
          (currentRate) => currentRate.apr === parseInt(apr, 10),
        ),
      );
      form.setValue('duration', duration.toString());
    }
  }, [unfilteredRates, apr, form, rates, bnplRates]);

  const onSubmit = () => {};

  return {
    rates,
    bnplRates,
    aprs,
    form,
    onSubmit,
  };
}
